import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AppLayout from '../../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getScoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}

function groupByMonthYear(items) {
  const groups = {};
  items.forEach(item => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${month}`;
    if (!groups[key]) groups[key] = { year, month, items: [] };
    groups[key].items.push(item);
  });
  return Object.values(groups).sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month - a.month
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  newBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '10px 20px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  yearLabel: {
    fontSize: '12px', fontWeight: '700', color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '1px',
    marginBottom: '6px', marginTop: '16px',
  },
  groupHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
    marginBottom: '6px', userSelect: 'none',
  },
  groupHeaderOpen: { backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe' },
  groupHeaderClosed: { backgroundColor: '#fff', border: '1px solid #e5e7eb' },
  groupTitle: { fontSize: '14px', fontWeight: '700', color: '#111827', flex: 1 },
  groupCount: {
    fontSize: '11px', fontWeight: '700', color: '#0052CC',
    backgroundColor: '#dbeafe', padding: '2px 8px', borderRadius: '20px',
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px', marginBottom: '12px',
  },
  card: {
    backgroundColor: '#fff', borderRadius: '10px', padding: '16px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  cardTitle: { fontSize: '14px', fontWeight: '700', color: '#111827' },
  cardDate: { fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
  scoreBadge: {
    fontSize: '16px', fontWeight: '800', padding: '4px 10px',
    borderRadius: '8px', display: 'inline-block',
  },
  cardMeta: { fontSize: '12px', color: '#6b7280', marginBottom: '12px' },
  goals: {
    fontSize: '12px', color: '#0052CC', backgroundColor: '#EBF5FF',
    padding: '5px 8px', borderRadius: '6px', marginBottom: '12px',
    fontWeight: '500',
  },
  statusBadge: {
    fontSize: '11px', fontWeight: '600', padding: '3px 8px',
    borderRadius: '20px', display: 'inline-block', marginBottom: '12px',
  },
  viewBtn: {
    width: '100%', backgroundColor: '#f3f4f6', color: '#374151',
    border: 'none', borderRadius: '6px', padding: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  empty: { textAlign: 'center', padding: '48px', color: '#6b7280', fontSize: '14px' },
  loading: { textAlign: 'center', padding: '48px', color: '#6b7280' },
};

export default function PracticeHistory() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => { fetchSessions(); }, []);

  async function fetchSessions() {
    try {
      const res = await fetch(`${API}/api/practice/sessions?owner_id=${currentUser.uid}`);
      const data = await res.json();
      const sorted = Array.isArray(data) ? data : [];
      setSessions(sorted);

      // Auto-open most recent group
      const groups = groupByMonthYear(sorted);
      if (groups.length > 0) {
        const first = groups[0];
        setOpenGroups({ [`${first.year}-${first.month}`]: true });
      }
    } finally {
      setLoading(false);
    }
  }

  function toggleGroup(key) {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const groups = groupByMonthYear(sessions);
  const byYear = {};
  groups.forEach(g => {
    if (!byYear[g.year]) byYear[g.year] = [];
    byYear[g.year].push(g);
  });
  const sortedYears = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Practice History</div>
          <div style={styles.sub}>Your past practice sessions and performance scores</div>
        </div>
        <button style={styles.newBtn} onClick={() => navigate('/practice')}>+ New Session</button>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : sessions.length === 0 ? (
        <div style={styles.empty}>
          No practice sessions yet.{' '}
          <span
            style={{ color: '#0052CC', cursor: 'pointer', fontWeight: '600' }}
            onClick={() => navigate('/practice')}
          >
            Start your first session →
          </span>
        </div>
      ) : (
        sortedYears.map(year => (
          <div key={year}>
            <div style={styles.yearLabel}>{year}</div>
            {byYear[year].map(group => {
              const key = `${group.year}-${group.month}`;
              const isOpen = !!openGroups[key];
              return (
                <div key={key}>
                  <div
                    style={{ ...styles.groupHeader, ...(isOpen ? styles.groupHeaderOpen : styles.groupHeaderClosed) }}
                    onClick={() => toggleGroup(key)}
                  >
                    <div style={styles.groupTitle}>{MONTHS[group.month]}</div>
                    <span style={styles.groupCount}>
                      {group.items.length} session{group.items.length !== 1 ? 's' : ''}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{isOpen ? '▲' : '▼'}</span>
                  </div>

                  {isOpen && (
                    <div style={styles.grid}>
                      {group.items.map(s => (
                        <div key={s.id} style={styles.card}>
                          <div style={styles.cardTop}>
                            <div>
                              <div style={styles.cardTitle}>{s.template_title}</div>
                              <div style={styles.cardDate}>
                                {new Date(s.created_at).toLocaleDateString()} · {s.turn_count || 0} turns
                              </div>
                            </div>
                            {s.evaluation?.overall_score != null && (
                              <div style={{
                                ...styles.scoreBadge,
                                backgroundColor: `${getScoreColor(s.evaluation.overall_score)}15`,
                                color: getScoreColor(s.evaluation.overall_score),
                              }}>
                                {s.evaluation.overall_score}
                              </div>
                            )}
                          </div>

                          {s.goals && (
                            <div style={styles.goals}>🎯 {s.goals}</div>
                          )}

                          <div style={styles.cardMeta}>
                            {s.llm_provider} · {s.stt_provider}
                          </div>

                          <div style={{
                            ...styles.statusBadge,
                            backgroundColor: s.status === 'completed' ? '#dcfce7' : '#fef3c7',
                            color: s.status === 'completed' ? '#16a34a' : '#d97706',
                          }}>
                            {s.status}
                          </div>

                          <button
                            style={styles.viewBtn}
                            onClick={() => navigate(
                              s.status === 'completed'
                                ? `/practice/report/${s.id}`
                                : `/practice/session/${s.id}`
                            )}
                          >
                            {s.status === 'completed' ? 'View Report →' : 'Resume Session →'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </AppLayout>
  );
}
